import { getActiveActorStates } from '@/utils/xstate-inspector'
import * as Sentry from '@sentry/react'
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo)

    const machineStates = getActiveActorStates()
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack, machineStates },
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="wefa-error-boundary">
          <div className="wefa-error-boundary__card">
            <h1 className="wefa-error-boundary__title">Oops! Something got mixed up.</h1>
            <p className="wefa-error-boundary__copy">Let&apos;s try that again.</p>
            {import.meta.env.DEV && this.state.error ? (
              <p className="wefa-status wefa-status--danger">{this.state.error.message}</p>
            ) : null}
            <button
              className="wefa-button wefa-button--primary wefa-button--block"
              onClick={() => {
                this.setState({ hasError: false, error: null })
                window.location.reload()
              }}
              type="button"
            >
              Try Again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
